/**
 * Plausible Analytics — cookieless, GDPR-by-design, no PII collected.
 * This is the provider already named in the Privacy Policy (src/pages/LegalPage.tsx,
 * Section 6) and the cookie-consent store (src/store/cookieStore.ts), but until
 * now no script actually loaded it. Gated on the visitor's `analytics` cookie
 * preference — never loads before consent is given.
 */

const PLAUSIBLE_DOMAIN = (import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined) || 'aiphra.com';
const SCRIPT_ID = 'plausible-script';

type PlausibleFn = ((event: string, options?: { props?: Record<string, string | number | boolean> }) => void) & {
  q?: unknown[];
};

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

function queuePlausible() {
  // Standard Plausible snippet shim: queues calls until the real script loads.
  if (window.plausible) return;
  const fn: PlausibleFn = (...args: unknown[]) => { (fn.q = fn.q || []).push(args); };
  window.plausible = fn;
}

/** Injects the Plausible script tag once. Safe to call multiple times. */
export function loadAnalytics() {
  if (document.getElementById(SCRIPT_ID)) return;
  queuePlausible();

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.defer = true;
  // data-manual: we drive pageviews ourselves (see trackPageview) so SPA route
  // changes are counted — the default snippet only fires once, on script load.
  script.dataset.domain = PLAUSIBLE_DOMAIN;
  script.dataset.manual = '';
  script.src = 'https://plausible.io/js/script.manual.js';
  document.head.appendChild(script);
}

export function unloadAnalytics() {
  document.getElementById(SCRIPT_ID)?.remove();
  delete window.plausible;
}

export function trackPageview() {
  window.plausible?.('pageview');
}

/** Custom conversion events — signup, agent view, checkout start, etc. No-ops if analytics isn't loaded/consented. */
export function trackEvent(name: string, props?: Record<string, string | number | boolean>) {
  window.plausible?.(name, props ? { props } : undefined);
}
